import { Button } from '@heidi/design-system/button';
import { Popover, PopoverAnchor, PopoverContent } from '@heidi/design-system/popover';
import { TypographyH4, TypographyP2 } from '@heidi/design-system/typography';
import { useTranslations } from '@heidi/runtime/i18n';
import { useAtom, useSetAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  confirmedEvidenceCopyChatIdsAtom,
  evidenceCopyConfirmationModalAtom,
} from '../../data';

import type { EvidenceCopyConfirmationPopoverHost } from './evidence-copy-confirmation-popover.host';

/**
 * Evidence copy-to-clipboard PHI/PII confirmation popover (EVI-1452 / SWR-88).
 *
 * Modeled after `ReviewConfirmationPopover` but Evidence-specific:
 * - PHI/PII attestation copy (distinct from Scribe's accuracy-review wording).
 * - Per-chat confirmation persisted client-side via `confirmedEvidenceCopyChatIdsAtom`.
 * - Analytics audit trail (`evidence_copy_confirmation` shown/confirmed/dismissed)
 *   to satisfy SWR-88's "logged confirmation" requirement.
 *
 * Singleton: mounted once globally and anchored to whichever copy button most recently
 * opened the gate (stored in `evidenceCopyConfirmationModalAtom.anchorEl`). Mount in
 * `GlobalModalsWrapper` so it sits above all chat content regardless of which Reply
 * actions row opened it.
 */
export const EvidenceCopyConfirmationPopover = ({
  host,
}: {
  host: EvidenceCopyConfirmationPopoverHost;
}) => {
  const { t } = useTranslations();
  const trackCopyConfirmation = host.useTrackCopyConfirmation();
  const [modalState, setModalState] = useAtom(evidenceCopyConfirmationModalAtom);
  const setConfirmedChatIds = useSetAtom(confirmedEvidenceCopyChatIdsAtom);

  // PopoverAnchor's `virtualRef` expects a `{ current: HTMLElement | null }`. Build
  // a fresh ref object only when the underlying anchor element changes — that way
  // Radix re-measures positioning when the user opens the popover against a different
  // copy button, but stays stable across unrelated re-renders.
  const virtualAnchorRef = useMemo<{ current: HTMLElement | null }>(
    () => ({ current: modalState.anchorEl }),
    [modalState.anchorEl]
  );

  // Fire the `shown` analytics event once per open. Keyed by chatId so that
  // closing + reopening (e.g. on a different message) emits a new event.
  const lastShownChatIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!modalState.isOpen) {
      lastShownChatIdRef.current = null;
      return;
    }
    const { chatId, messageId, surface } = modalState;
    if (!chatId || !surface || lastShownChatIdRef.current === chatId) {
      return;
    }
    lastShownChatIdRef.current = chatId;
    trackCopyConfirmation('shown', surface, chatId, messageId);
  }, [modalState, trackCopyConfirmation]);

  const resetModal = useCallback(() => {
    setModalState({
      isOpen: false,
      onConfirm: null,
      chatId: null,
      messageId: null,
      surface: null,
      anchorEl: null,
    });
  }, [setModalState]);

  const handleDismiss = useCallback(() => {
    const { chatId, messageId, surface } = modalState;
    if (chatId && surface) {
      trackCopyConfirmation('dismissed', surface, chatId, messageId);
    }
    resetModal();
  }, [modalState, resetModal, trackCopyConfirmation]);

  const handleConfirm = useCallback(() => {
    const { onConfirm, chatId, messageId, surface } = modalState;
    if (chatId) {
      setConfirmedChatIds((prev) => ({ ...prev, [chatId]: true }));
      if (surface) {
        trackCopyConfirmation('confirmed', surface, chatId, messageId);
      }
    }
    if (onConfirm) {
      onConfirm();
    }
    resetModal();
  }, [modalState, resetModal, setConfirmedChatIds, trackCopyConfirmation]);

  return (
    <Popover
      open={modalState.isOpen}
      onOpenChange={(open) => {
        if (!open) {
          handleDismiss();
        }
      }}
    >
      {modalState.isOpen && (
        // Backdrop is conditionally rendered so the `data-[state=closed]` Tailwind
        // variants would never activate — keep only the `data-[state=open]` enter
        // animation. Use the semantic `bg-overlay` token so the backdrop flips
        // correctly between newBrand and newBrandDark themes.
        <div
          className="fixed inset-0 z-50 bg-overlay data-[state=open]:animate-in data-[state=open]:fade-in-0"
          data-state="open"
          aria-hidden="true"
        />
      )}
      <PopoverAnchor virtualRef={virtualAnchorRef} />
      <PopoverContent
        side="top"
        align="end"
        sideOffset={8}
        className="w-96 space-y-4 p-4"
        onInteractOutside={(e) => {
          // Suppress outside-click dismissal: the user must explicitly choose
          // Confirm or Cancel so the PHI/PII attestation cannot be sidestepped
          // by an accidental click.
          e.preventDefault();
        }}
        onEscapeKeyDown={(e) => {
          // Treat Escape as an explicit dismissal rather than swallowing it —
          // suppressing without acting would be a WCAG 2.1.2 keyboard trap.
          // We still prevent the default Radix close path so our dismiss
          // analytics event fires before the popover unmounts.
          e.preventDefault();
          handleDismiss();
        }}
        data-testid="evidence-copy-confirmation-popover"
      >
        <div className="space-y-3">
          <TypographyH4>{t('evidenceCopyConfirmation.title')}</TypographyH4>
          <TypographyP2 className="text-text-secondary">
            {t('evidenceCopyConfirmation.body')}
          </TypographyP2>
        </div>
        <div className="flex justify-end gap-2">
          <Button
            disableAutomaticSvgSizing
            onClick={handleDismiss}
            size="md"
            variant="ghost"
            data-testid="evidence-copy-confirmation-dismiss"
          >
            {t('evidenceCopyConfirmation.dismiss')}
          </Button>
          <Button
            disableAutomaticSvgSizing
            size="md"
            onClick={handleConfirm}
            data-testid="evidence-copy-confirmation-confirm"
          >
            {t('evidenceCopyConfirmation.confirm')}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};
